var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var _canvas, _rc, _rafId, _context, _controller, _transMatrix, _transOrigin, _roughOptions, _dim3To2Mul, _paused, _hamvas, _startRotateX, _startRotateY, _rotateX, _rotateY, _translateX, _translateY, _translateZ, _startScale2D, _scale2D, _boundOnWheel, _boundOnResize, _viewHeight_2, _margin;
import rough from "roughjs/bin/rough";
import { JugglerVertex } from "../juggler";
import { Vec3D } from "../vec3d";
import Hammer from "hammerjs";
import { Matrix3D } from "../matrix3d";
const JugglerColors = ["orange", "purple", "green", "yellow", "orange"];
const PropColors = [
    "red",
    "green",
    "blue",
    "yellow",
    "purple",
    "black",
    "pink",
    "white",
    "orange",
];
function mulberry32(a) {
    return function () {
        var t = (a += 0x6d2b79f5);
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}
export class Canvas2DRenderer {
    constructor(el, margin) {
        _canvas.set(this, void 0);
        _rc.set(this, void 0);
        _rafId.set(this, 0);
        _context.set(this, void 0);
        _controller.set(this, void 0);
        _transMatrix.set(this, void 0);
        _transOrigin.set(this, new Vec3D());
        _roughOptions.set(this, {
            seed: 1,
            strokeWidth: 4,
            fillWeight: 1,
            hachureGap: 4,
            bowing: 5,
            roughness: 1.2,
            fill: "blue",
        });
        _dim3To2Mul.set(this, 1);
        _paused.set(this, false);
        _hamvas.set(this, void 0);
        _startRotateX.set(this, 0);
        _startRotateY.set(this, 0);
        _rotateX.set(this, 0);
        _rotateY.set(this, 0);
        _translateX.set(this, 0);
        _translateY.set(this, -50);
        _translateZ.set(this, 0);
        _startScale2D.set(this, 1);
        _scale2D.set(this, 1);
        _boundOnWheel.set(this, void 0);
        _boundOnResize.set(this, void 0);
        _viewHeight_2.set(this, 150);
        _margin.set(this, void 0);
        __classPrivateFieldSet(this, _margin, margin);
        __classPrivateFieldSet(this, _canvas, el);
        __classPrivateFieldSet(this, _context, el.getContext("2d"));
        __classPrivateFieldSet(this, _rc, rough.canvas(el));
        __classPrivateFieldSet(this, _transMatrix, new Matrix3D());
        this.raf();
        __classPrivateFieldSet(this, _hamvas, new Hammer(el));
        __classPrivateFieldGet(this, _hamvas).get("pan").set({
            direction: Hammer.DIRECTION_ALL,
            threshold: 0,
        });
        __classPrivateFieldGet(this, _hamvas).on("panstart", (e) => this.onPanStart(e));
        __classPrivateFieldGet(this, _hamvas).on("pan", (e) => this.onPan(e));
        __classPrivateFieldGet(this, _hamvas).get("pinch").set({ enable: true });
        __classPrivateFieldGet(this, _hamvas).on("pinchstart", (e) => this.onPinchStart(e));
        __classPrivateFieldGet(this, _hamvas).on("pinch", (e) => this.onPinch(e));
        __classPrivateFieldSet(this, _boundOnWheel, this.onWheel.bind(this));
        __classPrivateFieldGet(this, _canvas).addEventListener("wheel", __classPrivateFieldGet(this, _boundOnWheel));
        __classPrivateFieldSet(this, _boundOnResize, this.onResize.bind(this));
        window.addEventListener("resize", __classPrivateFieldGet(this, _boundOnResize));
    }
    onResize(e) {
        this.raf();
    }
    onWheel(e) {
        let scale = Math.pow(0.99, e.deltaY);
        __classPrivateFieldSet(this, _scale2D, __classPrivateFieldGet(this, _scale2D) * scale);
        // To prevent elastic bounce on Safari@Mac:
        e.preventDefault();
        this.raf();
    }
    onPanStart(e) {
        __classPrivateFieldSet(this, _startRotateX, __classPrivateFieldGet(this, _rotateX));
        __classPrivateFieldSet(this, _startRotateY, __classPrivateFieldGet(this, _rotateY));
    }
    onPan(e) {
        __classPrivateFieldSet(this, _rotateY, __classPrivateFieldGet(this, _startRotateY) + (2 * (Math.PI * e.deltaX)) / 360);
        __classPrivateFieldSet(this, _rotateX, __classPrivateFieldGet(this, _startRotateX) - (2 * (Math.PI * e.deltaY)) / 360);
        this.raf();
    }
    onPinchStart(e) {
        __classPrivateFieldSet(this, _startScale2D, __classPrivateFieldGet(this, _scale2D));
    }
    onPinch(e) {
        __classPrivateFieldSet(this, _scale2D, __classPrivateFieldGet(this, _startScale2D) * e.scale);
        this.raf();
    }
    setupCamera() {
        __classPrivateFieldSet(this, _transMatrix, new Matrix3D());
        __classPrivateFieldSet(this, _transMatrix, __classPrivateFieldGet(this, _transMatrix).rotate(__classPrivateFieldGet(this, _rotateX), __classPrivateFieldGet(this, _rotateY), 0));
        __classPrivateFieldSet(this, _transMatrix, __classPrivateFieldGet(this, _transMatrix).translate(new Vec3D(__classPrivateFieldGet(this, _translateX), __classPrivateFieldGet(this, _translateY), __classPrivateFieldGet(this, _translateZ))));
        __classPrivateFieldSet(this, _transOrigin, __classPrivateFieldGet(this, _transMatrix).transform(new Vec3D()));
        __classPrivateFieldSet(this, _dim3To2Mul, this.dim3To2(1));
        __classPrivateFieldGet(this, _roughOptions).strokeWidth = __classPrivateFieldGet(this, _dim3To2Mul);
    }
    step(timestamp) {
        if (!__classPrivateFieldGet(this, _controller))
            return;
        const scene = __classPrivateFieldGet(this, _controller).onFrame(this, timestamp);
        this.wipeFrame();
        this.setupCamera();
        const elements = [];
        for (const prop of scene.props) {
            elements.push(this.renderProp(prop));
        }
        for (let j = 0; j < scene.jugglers.length; j++) {
            const juggler = scene.jugglers[j];
            const jugglerVertices = this.transform(juggler.getTransformedVertices());
            elements.push(this.renderHead(j, jugglerVertices));
            elements.push(this.renderTorso(j, jugglerVertices));
            elements.push(this.renderArmLine(jugglerVertices[JugglerVertex.LeftShoulder], jugglerVertices[JugglerVertex.LeftElbow]));
            elements.push(this.renderArmLine(jugglerVertices[JugglerVertex.LeftElbow], jugglerVertices[JugglerVertex.LeftHand]));
            elements.push(this.renderArmLine(jugglerVertices[JugglerVertex.RightShoulder], jugglerVertices[JugglerVertex.RightElbow]));
            elements.push(this.renderArmLine(jugglerVertices[JugglerVertex.RightElbow], jugglerVertices[JugglerVertex.RightHand]));
        }
        elements.sort((a, b) => -(a.z - b.z));
        for (const element of elements) {
            element.render();
        }
        this.rafLoop();
    }
    transform(vertices) {
        const ret = new Array(vertices.length);
        for (let i = 0; i < vertices.length; i++) {
            ret[i] = __classPrivateFieldGet(this, _transMatrix).transform(vertices[i]);
        }
        return ret;
    }
    renderProp(prop) {
        const pos = __classPrivateFieldGet(this, _transMatrix).transform(prop.position);
        return {
            z: pos.z,
            render: () => {
                const cprop = this.project(pos);
                const diameter = __classPrivateFieldGet(this, _dim3To2Mul) * prop.diameter;
                const ctx = __classPrivateFieldGet(this, _context);
                ctx.save();
                ctx.translate(cprop[0], cprop[1]);
                if (prop.path) {
                    const rotations = 10 * mulberry32(prop.id)();
                    const rad = (rotations * Math.PI * 2 * prop.pathMillis) /
                        (prop.path.endT - prop.path.startT);
                    ctx.rotate(rad);
                }
                __classPrivateFieldGet(this, _rc).circle(0, 0, diameter, Object.assign(Object.assign({}, __classPrivateFieldGet(this, _roughOptions)), { stroke: "black", fill: PropColors[prop.id % PropColors.length], fillStyle: "solid", roughness: 1.5 }));
                ctx.restore();
            },
        };
    }
    renderArmLine(f, t) {
        return {
            z: (f.z + t.z) / 2,
            render: () => {
                const f2 = this.project(f);
                const t2 = this.project(t);
                __classPrivateFieldGet(this, _rc).line(f2[0], f2[1], t2[0], t2[1], Object.assign(Object.assign({}, __classPrivateFieldGet(this, _roughOptions)), { stroke: "black", bowing: 10 }));
            },
        };
    }
    renderHead(jn, j) {
        const numPoints = 24;
        const points = new Array(numPoints);
        const center = j[JugglerVertex.HeadCenter];
        const origin = __classPrivateFieldGet(this, _transOrigin);
        const dx = j[JugglerVertex.HeadDX].sub(origin);
        const dy = j[JugglerVertex.HeadDY].sub(origin);
        for (let p = 0; p < numPoints; p++) {
            const rad = (p * Math.PI * 2) / numPoints;
            const cos = Math.cos(rad);
            const sin = Math.sin(rad);
            const v = new Vec3D(center.x + cos * dx.x - sin * dy.x, center.y + cos * dx.y - sin * dy.y, center.z + cos * dx.z - sin * dy.z);
            points[p] = this.project(v);
        }
        return {
            z: j[JugglerVertex.HeadCenter].z,
            render: () => {
                const ctx = __classPrivateFieldGet(this, _context);
                ctx.fillStyle = "white";
                ctx.beginPath();
                let first = true;
                for (const pt of points) {
                    first ? ctx.moveTo(pt[0], pt[1]) : ctx.lineTo(pt[0], pt[1]);
                    first = false;
                }
                ctx.fill();
                __classPrivateFieldGet(this, _rc).polygon(points, Object.assign(Object.assign({}, __classPrivateFieldGet(this, _roughOptions)), { fill: "red" }));
            },
        };
    }
    renderTorso(jn, j) {
        const lh = j[JugglerVertex.LeftHip];
        const rh = j[JugglerVertex.RightHip];
        const rs = j[JugglerVertex.RightShoulder];
        const ls = j[JugglerVertex.LeftShoulder];
        const poly = [
            this.project(lh),
            this.project(rh),
            this.project(rs),
            this.project(ls),
        ];
        return {
            z: (lh.z + rh.z + ls.z + rs.z) / 4,
            render: () => {
                const ctx = __classPrivateFieldGet(this, _context);
                ctx.beginPath();
                ctx.fillStyle = "white";
                ctx.moveTo(poly[0][0], poly[0][1]);
                ctx.lineTo(poly[1][0], poly[1][1]);
                ctx.lineTo(poly[2][0], poly[2][1]);
                ctx.lineTo(poly[3][0], poly[3][1]);
                ctx.closePath();
                ctx.fill();
                __classPrivateFieldGet(this, _rc).polygon(poly, Object.assign(Object.assign({}, __classPrivateFieldGet(this, _roughOptions)), { fill: JugglerColors[jn % JugglerColors.length] }));
            },
        };
    }
    project(v) {
        const [mt, mr, mb, ml] = __classPrivateFieldGet(this, _margin);
        const canvasWidth_2 = (__classPrivateFieldGet(this, _canvas).width - mr - ml) / 2;
        const canvasHeight_2 = (__classPrivateFieldGet(this, _canvas).height - mt - mb) / 2;
        const x = ml +
            canvasWidth_2 +
            (__classPrivateFieldGet(this, _scale2D) * canvasHeight_2 * v.x) / __classPrivateFieldGet(this, _viewHeight_2);
        const y = mt +
            canvasHeight_2 -
            (__classPrivateFieldGet(this, _scale2D) * canvasHeight_2 * v.y) / __classPrivateFieldGet(this, _viewHeight_2);
        return [x, y];
    }
    dim3To2(d) {
        const o = this.project(__classPrivateFieldGet(this, _transOrigin));
        const r = this.project(__classPrivateFieldGet(this, _transOrigin).add(new Vec3D(d, 0, 0)));
        return r[0] - o[0];
    }
    pickupCanvasDimensions() {
        if (__classPrivateFieldGet(this, _canvas).width !== __classPrivateFieldGet(this, _canvas).clientWidth ||
            __classPrivateFieldGet(this, _canvas).height !== __classPrivateFieldGet(this, _canvas).clientHeight) {
            __classPrivateFieldGet(this, _canvas).width = __classPrivateFieldGet(this, _canvas).clientWidth;
            __classPrivateFieldGet(this, _canvas).height = __classPrivateFieldGet(this, _canvas).clientHeight;
        }
    }
    wipeFrame() {
        this.pickupCanvasDimensions();
        __classPrivateFieldGet(this, _context).clearRect(0, 0, __classPrivateFieldGet(this, _canvas).width, __classPrivateFieldGet(this, _canvas).height);
    }
    rafLoop() {
        if (__classPrivateFieldGet(this, _paused))
            return;
        this.raf();
    }
    raf() {
        this.cancelRAF();
        __classPrivateFieldSet(this, _rafId, window.requestAnimationFrame(this.step.bind(this)));
    }
    cancelRAF() {
        window.cancelAnimationFrame(__classPrivateFieldGet(this, _rafId));
    }
    destroy() {
        this.cancelRAF();
        __classPrivateFieldGet(this, _hamvas).destroy();
        __classPrivateFieldGet(this, _canvas).removeEventListener("wheel", __classPrivateFieldGet(this, _boundOnWheel));
        window.removeEventListener("resize", __classPrivateFieldGet(this, _boundOnResize));
    }
    setController(ctrl) {
        __classPrivateFieldSet(this, _controller, ctrl);
    }
    setPaused(paused) {
        const wasPaused = __classPrivateFieldGet(this, _paused);
        __classPrivateFieldSet(this, _paused, paused);
        if (wasPaused) {
            this.raf();
        }
    }
    zoomToFit(bbox) {
        this.pickupCanvasDimensions();
        const cy = (bbox.min.y + bbox.max.y) / 2;
        __classPrivateFieldSet(this, _scale2D, 1);
        __classPrivateFieldSet(this, _translateY, -cy);
        __classPrivateFieldSet(this, _viewHeight_2, (bbox.max.y - bbox.min.y) / 2);
        // The height will always fit now, but the width might not. Check if we need
        // to down scale.
        const [cx] = this.project(bbox.min);
        if (cx < 0) {
            __classPrivateFieldSet(this, _scale2D, __classPrivateFieldGet(this, _canvas).width / (__classPrivateFieldGet(this, _canvas).width - 2 * cx));
        }
    }
}
_canvas = new WeakMap(), _rc = new WeakMap(), _rafId = new WeakMap(), _context = new WeakMap(), _controller = new WeakMap(), _transMatrix = new WeakMap(), _transOrigin = new WeakMap(), _roughOptions = new WeakMap(), _dim3To2Mul = new WeakMap(), _paused = new WeakMap(), _hamvas = new WeakMap(), _startRotateX = new WeakMap(), _startRotateY = new WeakMap(), _rotateX = new WeakMap(), _rotateY = new WeakMap(), _translateX = new WeakMap(), _translateY = new WeakMap(), _translateZ = new WeakMap(), _startScale2D = new WeakMap(), _scale2D = new WeakMap(), _boundOnWheel = new WeakMap(), _boundOnResize = new WeakMap(), _viewHeight_2 = new WeakMap(), _margin = new WeakMap();
